import React, { FC, useEffect, useState } from 'react';
import styles from './newOrganisationModal.module.scss';
import { Col, Form, Modal, Row } from 'antd';
import { Formik } from 'formik';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import ParentCompanySetupService from 'services/ParentCompanySetup/parentCompanySetup.service';
import { Organization } from 'models/CompanySetup/organization.model';
import { NewOrganisationModalEnum } from 'enums/organisationMapEnums';
import { NewOrganisationModalProps } from './addNewOrganisation.utls';
import {
  editOrganizationValidationSchema,
  orgValidationSchema,
} from '../newOrganisationValidationSchema';
import CancelFormModal from 'shared/components/CancelFormModal';
import { CompanySetupEnum } from 'enums/CompanySetupEnums';
import OrganisationFormFields from './organisationFormFields';
import COButton from 'shared/components/COButton';
import { ButtonSize } from 'enums/ButtonEnums';
import { OrganizationDetails } from 'models/OrganizationDetails/organizationDetails.model';
import { useParentOrganizationContext } from 'context/ParentOrganisationContext';
import { organizationalBoundary } from 'views/CompanySetup/companySetupConstants';
import { Size } from 'models/CompanySetup/size.model';
import { Sector } from 'models/CompanySetup/sector.model';
import { CompanyType } from 'models/CompanySetup/companyType.model';
import { modalCloseIcon } from 'shared/components/OrganizationModal';
import MetadataService from 'services/ParentCompanySetup/metadata.service';

const NewOrganisationModal: FC<NewOrganisationModalProps> = ({
  isOpen,
  onClose,
  isMainParent,
  updateOrganisation,
  organisationList,
  data,
  handleSubmit,
  loading,
}) => {
  const { sectorMetaData, fetchSector } = ParentCompanySetupService();
  const {
    sizeMetaData,
    fetchSizeMetaData,
    fetchCompanyTypesMeta,
    companyTypesMeta,
  } = MetadataService();
  useEffect(() => {
    fetchSizeMetaData();
    fetchCompanyTypesMeta();
  }, []);
  const { organizationData } = useParentOrganizationContext();

  const [activeTab, setActiveTab] = useState<NewOrganisationModalEnum>(
    NewOrganisationModalEnum.ORGANIZATION_DETAILS
  );
  const [deleteVisibility, setDeleteVisibility] = useState<boolean>(false);

  const isOrganisationDetailsTab = !!(
    isMainParent && activeTab === NewOrganisationModalEnum.ORGANIZATION_DETAILS
  );

  //TODO - handle new org creation
  const onSubmit = (values: Organization) => {
    if (!updateOrganisation || !values.name) return;
    const newOrganisation = {
      ...values,
      organisationYearWise: {
        ...values.organisationYearWise,
        isActive: true,
      },
    };
    delete newOrganisation.company?.baselineYear;
    delete newOrganisation.company?.reportingMonth;
    updateOrganisation({
      organisation: newOrganisation,
    });
    onClose();
  };

  const toggleDeleteModalVisibility = () => setDeleteVisibility(prev => !prev);
  const next = () => setActiveTab(NewOrganisationModalEnum.REPORTING_DETAILS);
  const prev = () =>
    setActiveTab(NewOrganisationModalEnum.ORGANIZATION_DETAILS);
  const footer = (submitForm: () => void, dirty: boolean) => {
    return (
      <Row
        gutter={[15, 15]}
        className={styles.validationFooter}
        justify="center">
        <Col span={12}>
          <COButton
            primary
            loading={loading}
            size={ButtonSize.LARGE}
            submit
            onClick={isOrganisationDetailsTab ? next : submitForm}>
            {isOrganisationDetailsTab
              ? NewOrganisationModalEnum.PROCEED_TEXT
              : NewOrganisationModalEnum.SUBMIT_TEXT}
          </COButton>
        </Col>
        <Col span={12}>
          <COButton
            secondary
            size={ButtonSize.LARGE}
            onClick={
              isMainParent &&
              activeTab === NewOrganisationModalEnum.REPORTING_DETAILS
                ? prev
                : toggleDeleteModalVisibility
            }>
            {NewOrganisationModalEnum.CLOSE_TEXT}
          </COButton>
        </Col>
      </Row>
    );
  };

  const handleClose = () => {
    toggleDeleteModalVisibility();
    setActiveTab(NewOrganisationModalEnum.ORGANIZATION_DETAILS);
    onClose();
  };

  const editSubmit = async (formValues: OrganizationDetails) => {
    if (!handleSubmit) return;
    const response = await handleSubmit(formValues);
    if (!response) return;
    prev();
    onClose();
  };

  const newOrganizationData = (): Organization => {
    return {
      ...new Organization(),
      sector: new Sector(),
      size: new Size(),
      companyType: new CompanyType(),
      ...(isMainParent && {
        company: {
          organizationalBoundary:
            organizationData?.company?.organizationalBoundary ??
            organizationalBoundary.operational,
          recalculationThreshold:
            organizationData?.company?.recalculationThreshold,
          baselineYear: organizationData?.company?.baselineYear,
          reportingMonth: organizationData?.company?.reportingMonth
            ? String(organizationData?.company?.reportingMonth)
            : undefined,
        },
      }),
    };
  };

  const initialValues: OrganizationDetails = data
    ? {
        ...data,
        company: {
          ...data?.company,
          organizationalBoundary:
            organizationData?.company?.organizationalBoundary ??
            organizationalBoundary.operational,
          reportingMonth: data?.company?.reportingMonth
            ? String(data?.company?.reportingMonth)
            : undefined,
        },
      }
    : newOrganizationData();

  return (
    <div>
      <Formik
        initialValues={isOpen ? initialValues : {}}
        onSubmit={!!data?.organization?.id ? editSubmit : onSubmit}
        enableReinitialize
        validationSchema={
          data
            ? editOrganizationValidationSchema(isMainParent)
            : orgValidationSchema(isMainParent, organisationList)
        }>
        {({ submitForm, dirty }) => {
          return (
            <Form>
              <Modal
                destroyOnClose={true}
                maskClosable={false}
                centered
                width={500}
                footer={footer(submitForm, dirty)}
                wrapClassName={styles.modalContainer}
                open={isOpen}
                closeIcon={modalCloseIcon}
                onCancel={toggleDeleteModalVisibility}>
                <OrganisationFormFields
                  activeTab={activeTab}
                  sectorMetaData={sectorMetaData}
                  sizeMetaData={sizeMetaData}
                  isMainParent={isMainParent}
                  isOrganisationDetailsTab={isOrganisationDetailsTab}
                  fetchSector={fetchSector}
                  companyTypesMeta={companyTypesMeta}
                  data={data}
                />
                <CancelFormModal
                  handleModalCancel={toggleDeleteModalVisibility}
                  handleModalConfirm={handleClose}
                  showModalState={deleteVisibility}
                  heading={CompanySetupEnum.CloseFormHeading}
                  subHeading={CompanySetupEnum.CloseFormSubHeading}
                />
              </Modal>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default NewOrganisationModal;
